import { type Middleware, unstable_serialize } from 'swr';

declare module 'swr' {
  interface SWRResponse {
    /**
     * True while a request is in its automatic error-retry phase: it has
     * errored, and SWR has retries left (initial attempt +
     * `errorRetryCount` retries, unless `shouldRetryOnError` disables
     * them). Resets on the next successful fetch. Populated by the
     * `withRetryStatus` middleware; undefined outside its provider.
     */
    isRetrying?: boolean;
    /**
     * True once a request has errored and its automatic retries are
     * exhausted, until a fetch succeeds. Unlike `error`, this is a stable
     * boolean across background revalidations (each of which produces a
     * new error object), so it is safe to key one-shot side effects
     * (toasts, logging) on it in an effect dependency array. Populated by
     * the `withRetryStatus` middleware; undefined outside its provider.
     */
    isTerminalError?: boolean;
  }
}

// Keyed by SWR's serialized key and shared across hook instances: SWR fires
// onError only on the instance that ran the failed fetch, while every other
// subscriber of the same key receives the error through the shared cache. If
// exhaustion were tracked per instance, those subscribers would report
// isRetrying forever.
//
// Entries are deleted on success; entries for keys that never succeed are
// kept deliberately so the terminal state survives remounts (a few bytes per
// distinct failing key, per tab session).
const retryState = new Map<string, { attempts: number; exhausted: boolean }>();

/**
 * SWR middleware that reports whether a failed request is still being
 * retried automatically, so error UI can distinguish "still retrying"
 * (keep showing a loading state) from "given up" (show the error).
 *
 * It also keeps `isLoading` true through the retry phase while no data has
 * ever arrived. Bare SWR re-flips `isLoading` false/true on every retry,
 * which makes loading gates oscillate between loading and error states —
 * remounting their subtree and resetting retry backoff, producing an
 * outage refresh loop. Consumers throughout TXM treat `isLoading` as
 * "first load hasn't settled yet"; this aligns it with that assumption.
 */
export const withRetryStatus: Middleware =
  (useSWRNext) => (key, fetcher, config) => {
    const swr = useSWRNext(key, fetcher, {
      ...config,
      onError: (error, errorKey, errorConfig) => {
        const state = retryState.get(errorKey) ?? {
          attempts: 0,
          exhausted: false,
        };
        state.attempts += 1;
        const retryEnabled =
          typeof errorConfig.shouldRetryOnError === 'function'
            ? errorConfig.shouldRetryOnError(error)
            : (errorConfig.shouldRetryOnError ?? true);
        const maxRetries =
          errorConfig.errorRetryCount ?? Number.POSITIVE_INFINITY;
        state.exhausted = !retryEnabled || state.attempts > maxRetries;
        retryState.set(errorKey, state);
        config.onError?.(error, errorKey, errorConfig);
      },
      onSuccess: (data, successKey, successConfig) => {
        retryState.delete(successKey);
        config.onSuccess?.(data, successKey, successConfig);
      },
    });

    // Subscribers re-render whenever the cached error updates (every failed
    // attempt), so reading the shared state during render stays current.
    // unstable_serialize is SWR's public key serializer (the prefix warns the
    // format may change between SWR versions); we only round-trip keys within
    // one runtime, and the callbacks above receive keys serialized by the
    // same function, so a format change can't desynchronize the lookup.
    const exhausted =
      retryState.get(unstable_serialize(key))?.exhausted === true;
    const isRetrying = swr.error !== undefined && !exhausted;
    const isTerminalError = swr.error !== undefined && exhausted;

    // Hold isLoading through the retry phase, and pin it false once the
    // error is terminal: later revalidations (polling, focus) start fresh
    // SWR retry sequences whose in-flight attempts would otherwise re-flip
    // isLoading and oscillate gates between loading and error states.
    const isLoading = isTerminalError
      ? false
      : swr.isLoading || (isRetrying && swr.data === undefined);

    return { ...swr, isRetrying, isTerminalError, isLoading };
  };
